import React, { useState } from 'react';
import { usePageContext } from '../contexts';
import { Twitter, Linkedin, Github, Mail, Instagram, ArrowUpRight, Heart } from 'lucide-react';
import wobLogo from '../logos/wob.svg';

export const Footer = () => {
  const { currentPage, navigateTo } = usePageContext();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState(''); // 'success' or 'error'

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    // Reset status
    setStatusMessage('');
    setStatusType('');

    // Validate email
    if (!email.trim()) {
      setStatusMessage('Please enter your email address');
      setStatusType('error');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setStatusMessage('Please enter a valid email address');
      setStatusType('error');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatusMessage(data.message || 'Successfully subscribed!');
        setStatusType('success');
        setEmail(''); // Clear the input
      } else {
        setStatusMessage(data.error || 'Failed to subscribe. Please try again.');
        setStatusType('error');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setStatusMessage('An error occurred. Please try again later.');
      setStatusType('error');
    } finally {
      setIsLoading(false);
    }
  };

  if (currentPage === 'apply') return null;

  return (
    <footer className="relative bg-zinc-950 text-white border-t border-zinc-900 py-6 sm:py-8 overflow-hidden">
      {/* Subtle Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-900/10 to-zinc-950 pointer-events-none"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-6 sm:gap-4">
          {/* Logo & Copyright */}
          <div className="flex items-center gap-4">
            <div className="cursor-pointer" onClick={() => navigateTo('landing')}>
              <img
                src={wobLogo}
                alt="Panometra"
                className="h-5 sm:h-6 w-auto opacity-80 hover:opacity-100 transition-opacity duration-300"
              />
            </div>
            <span className="text-zinc-700 hidden sm:block">|</span>
            <p className="text-zinc-500 font-mono text-[10px] sm:text-xs uppercase tracking-widest">
              © 2026 PANOMETRA
            </p>
          </div>

          {/* Social Links Only */}
          <div className="flex items-center gap-6">
            <a
              href="https://linkedin.com/company/panometra"
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-500 hover:text-blue-500 transition-all transform hover:scale-110"
              title="LinkedIn"
            >
              <Linkedin size={18} />
            </a>
            <a
              href="https://instagram.com/panometra"
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-500 hover:text-white transition-all transform hover:scale-110"
              title="Instagram"
            >
              <Instagram size={18} />
            </a>
            <a
              href="mailto:hello@panometra.com"
              className="text-zinc-500 hover:text-orange-500 transition-all transform hover:scale-110"
              title="Email"
            >
              <Mail size={18} />
            </a>
          </div>

          {/* Legal Links */}
          <div className="flex gap-4 font-mono text-[10px] uppercase tracking-wider text-zinc-600">
            <a href="#" className="hover:text-zinc-400 transition-colors">Privacy</a>
            <a href="#" className="hover:text-zinc-400 transition-colors">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
